import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, CalendarCheck, PlayCircle, RotateCcw, CheckCircle2, AlertTriangle,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { Spinner }         from '@/components/ui/Spinner';
import { cn }              from '@/utils/cn';
import { yearEndCloseApi } from '@/api/yearEndClose.api';
import type { YearEndCloseDto } from '@/api/yearEndClose.api';
import { financialYearApi } from '@/api/financialYear.api';
import { AccountLookupField } from '@/components/erp/AccountLookupField';

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
/**
 * This screen called /finance/year-close/history, /finance/year-close/execute and
 * .../rollback — none of which exist — and posted { financialYear, notes } against a contract
 * that wants a financial year id and the retained earnings account to transfer the result into.
 * The year list itself was five calendar years counted back from today rather than the company's
 * financial years. Nothing here has ever reached the server; the history table was always empty
 * and Execute always failed into a console.error.
 *
 * YearEndCloseDto is the server's own shape, imported rather than restated.
 */
interface FinancialYearOption { id: string; yearCode: string; }

const STATUS_COLORS: Record<string, string> = {
  Completed: 'bg-teal-100 dark:bg-teal-900/30  text-teal-700 dark:text-teal-400',
  Failed:    'bg-red-100 dark:bg-red-900/30   text-red-700 dark:text-red-400',
  Running:   'bg-blue-100 dark:bg-blue-900/30  text-blue-700 dark:text-blue-400',
  RolledBack:'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
};

function readError(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { message?: string; errors?: { message: string }[] } } };
  return err.response?.data?.errors?.[0]?.message ?? err.response?.data?.message ?? fallback;
}

// â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const YearEndClosePage = () => {
  const { hasPermission } = usePermission();

  const canExecute = hasPermission(Permissions.YEAR_CLOSE_EXECUTE);
  const canReopen  = hasPermission(Permissions.YEAR_CLOSE_REOPEN);

  const [history,      setHistory]      = useState<YearEndCloseDto[]>([]);
  const [years,        setYears]        = useState<FinancialYearOption[]>([]);
  const [reAccountId,  setReAccountId]  = useState<string | null>(null);
  const [reCode,       setReCode]       = useState<string | null>(null);
  const [reName,       setReName]       = useState<string | null>(null);
  const [rollbackReason, setRollbackReason] = useState('');
  const [loading,      setLoading]      = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState<string | null>(null);
  const [rollbackId,   setRollbackId]   = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await yearEndCloseApi.getList();
      setHistory(res.data?.data ?? []);
      setError(null);
    } catch (err) {
      setError(readError(err, 'Could not load the year-end close history.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // The company's own financial years, not five calendar years counted back from today.
  useEffect(() => {
    financialYearApi.getList({ pageNumber: 1, pageSize: 50 })
      .then(r => setYears((r.data?.data?.items ?? []).map(y => ({ id: y.id, yearCode: y.yearCode }))))
      .catch(() => setError('Could not load the financial years.'));
  }, []);

  const handleExecute = async () => {
    if (!selectedYear)  { setError('Choose a financial year.'); return; }
    if (!reAccountId)   { setError('Choose the Retained Earnings account the result transfers into.'); return; }
    setError(null); setSuccess(null); setActionLoading(true);
    try {
      const res = await yearEndCloseApi.execute({
        financialYearId:             selectedYear,
        retainedEarningsAccountId:   reAccountId,
        retainedEarningsAccountCode: reCode ?? '',
        retainedEarningsAccountName: reName ?? '',
      });
      const done = res.data?.data;
      // Say what moved. "Executed successfully" on its own tells an accountant nothing they can
      // check, and the figure is the whole point of the exercise.
      setSuccess(done
        ? `${done.yearCode} closed. ${fmtMoney(done.retainedEarningsTransferred)} transferred to `
          + `${done.retainedEarningsAccountCode} ${done.retainedEarningsAccountName}.`
        : 'Year-end close executed.');
      loadHistory();
    } catch (err) {
      setError(readError(err, 'Year-end close failed. Please try again.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRollback = (id: string) => setRollbackId(id);

  const executeRollback = async () => {
    if (!rollbackId) return;
    const reason = rollbackReason.trim();
    if (!reason) { setError('Give a reason for reopening the year. It is required and audited.'); return; }
    setActionLoading(true);
    try {
      await yearEndCloseApi.rollback(rollbackId, reason);
      setSuccess('The year has been reopened and the closing journal reversed.');
      setRollbackId(null); setRollbackReason(''); setError(null);
      loadHistory();
    } catch (err) {
      setError(readError(err, 'Rollback failed.'));
    } finally {
      setActionLoading(false);
    }
  };

  const fmtMoney = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <CalendarCheck size={20} className="text-teal-600 dark:text-teal-400" />
              Year-End Close
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Execute the year-end financial close process
            </p>
          </div>
          <button
            onClick={loadHistory}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">

        {/* â”€â”€ Execute form â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        {canExecute && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-base font-semibold text-slate-800 dark:text-white mb-4">Execute Year-End Close</h2>

            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                <AlertTriangle size={16} className="shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg text-teal-700 dark:text-teal-400 text-sm">
                <CheckCircle2 size={16} className="shrink-0" />
                {success}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                  Financial Year <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedYear}
                  onChange={e => setSelectedYear(e.target.value)}
                  className="w-full text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2
                             bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="">Select financial year...</option>
                  {years.map(y => <option key={y.id} value={y.id}>{y.yearCode}</option>)}
                </select>
              </div>
              {/* Where the year's profit or loss lands. Required by the close, and the figure that
                  ends up here is what the Trial Balance is checked against afterwards. */}
              <AccountLookupField
                label="Retained Earnings account *"
                value={reAccountId} displayCode={reCode} displayName={reName}
                isPostingAccount
                onChange={(id, code, name) => { setReAccountId(id); setReCode(code); setReName(name); }}
                placeholder="Choose account…"
              />
            </div>

            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-400 text-xs">
              <AlertTriangle size={13} className="inline mr-1.5" />
              This will close all modules for the selected financial year, generate closing entries, and carry forward balances.
              Ensure all transactions have been posted and all sub-ledgers are reconciled before proceeding.
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={handleExecute}
                disabled={actionLoading || !selectedYear || !reAccountId}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
              >
                {actionLoading ? <RefreshCw size={15} className="animate-spin" /> : <PlayCircle size={15} />}
                Execute Year-End Close
              </button>
            </div>
          </div>
        )}

        {/* â”€â”€ History table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-base font-semibold text-slate-800 dark:text-white">Execution History</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="lg" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-400">
              <CalendarCheck size={32} className="mb-2 opacity-40" />
              <p className="text-sm">No year-end close history yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Financial Year</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Transferred</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Executed Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Reason / errors</th>
                  {canReopen && <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {history.map(h => (
                  <tr key={h.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                    <td className="px-4 py-3 font-semibold text-teal-700 dark:text-teal-400">{h.yearCode}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[h.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                        {h.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700 dark:text-slate-300 tabular-nums">
                      {fmtMoney(h.retainedEarningsTransferred)}
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                      {h.executedDate ? fmtDate(h.executedDate) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs max-w-[220px] truncate">
                      {h.rollbackReason ?? h.validationErrors ?? '—'}
                    </td>
                    {canReopen && (
                      <td className="px-4 py-3 text-right">
                        {h.status === 'Completed' && (
                          <button
                            onClick={() => handleRollback(h.id)}
                            disabled={actionLoading}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 rounded transition-colors ml-auto"
                          >
                            <RotateCcw size={11} /> Rollback
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Reopening a closed year reverses its closing journal and is audited, and the server
          requires a reason — which the old confirm box never asked for, so every rollback would
          have been refused even had the route existed. */}
      {rollbackId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-slate-800">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
              <AlertTriangle size={15} className="text-amber-500" />
              Reopen this financial year
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              The closing journal is reversed and the year is opened again. The reason is kept on
              the record.
            </p>
            <textarea
              autoFocus rows={3} value={rollbackReason}
              onChange={e => setRollbackReason(e.target.value)}
              placeholder="Why is the year being reopened?"
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setRollbackId(null); setRollbackReason(''); }}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">
                Cancel
              </button>
              <button onClick={executeRollback} disabled={actionLoading}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50">
                Reopen year
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

